import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Chapter } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Orbit — ${t.blog.eyebrow}`;
    axios
      .get(`${API}/blog/posts`)
      .then((res) => setPosts(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  return (
    <main data-testid="blog-page" className="pt-[76px]">
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-white py-20 lg:py-28">
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Chapter index="01" label={t.blog.eyebrow} />
            <h1 className="max-w-3xl font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl text-balance">
              {t.blog.title}
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">{t.blog.subtitle}</p>
          </Reveal>

          {!loading && posts.length === 0 && (
            <p data-testid="blog-empty" className="mt-16 text-sm text-slate-500">
              {t.blog.empty}
            </p>
          )}

          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.1}>
                <Link
                  to={`/ressources/${post.slug}`}
                  data-testid={`blog-card-${i}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(10,15,29,0.14)]"
                >
                  {post.cover_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title_fr}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-500">
                      {t.blog.publishedOn} {formatDate(post.created_at)}
                    </p>
                    <h2 className="mt-2 font-display text-lg font-bold leading-snug text-slate-900">
                      {(lang === "en" && post.title_en) || post.title_fr}
                    </h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                      {(lang === "en" && post.excerpt_en) || post.excerpt_fr}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
                      {t.blog.readMore}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
